import type { TournamentHostingSchema, TournamentSurfaceSchema, TournamentTravelingSchema } from "@teamz/common";

import AddIcon from "@/assets/add.svg";
import EditIcon from "@/assets/edit.svg";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { clubHosting, clubTraveling, playerPreferredSurface, tournamentHostingSchema, tournamentSurfaceSchema, tournamentTravelingSchema } from "@teamz/common";
import { type ComponentProps, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { RouterOutputs } from "@/lib/trpc";

import { useMe } from "@/context/user-context";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetForm, SheetHeader, SheetTitle } from "../ui/sheet";
import { ErrorField, RootField } from "../ui/text-field";

type TournamentSpecsType = "tournament-surface" | "tournament-hosting" | "tournament-traveling";

type SpecButtonProps = {
  data: string | null;
  spec: TournamentSpecsType;
};

const SpecButton = ({ ref, data, spec, className, ...rest }: ComponentProps<"button"> & SpecButtonProps) => {
  const { t } = useTranslation(["profile"]);
  const { user } = useMe();

  const labels: Record<TournamentSpecsType, string> = {
    "tournament-surface": t("profile:surface"),
    "tournament-hosting": t("profile:hosting"),
    "tournament-traveling": t("profile:traveling"),
  };

  return (
    <button
      type="button"
      className={cn(
        "flex h-24 w-full flex-col rounded-xl border border-neutral-400 px-3 py-4 text-start",
        data ? "bg-white" : "bg-neutral-50",
        `group data-[color=blue]:bg-blue-500 data-[color=orange]:bg-orange-500 `,
        className,
        user.role === "player" && "pointer-events-none",
      )}
      ref={ref}
      {...rest}
    >
      <div className="flex w-full items-center justify-between text-neutral-500 group-data-[color=blue]:text-neutral-450 group-data-[color=orange]:text-orange-300">
        <span className="w-4/5 text-sm font-normal leading-4 ">
          {labels[spec]}
        </span>
        {user.role === "club" && (
          <div className="shrink-0">
            {data
              ? <EditIcon className="size-5" />
              : <AddIcon className="size-6 text-orange-500" />}
          </div>
        )}
      </div>
      {data && (
        <span className="mt-auto font-semibold leading-[18px] text-neutral-900 group-data-[color=blue]:text-white group-data-[color=orange]:text-white">
          {tDyn(t, `profile:${data}`)}
        </span>
      )}
    </button>
  );
};

function TournamentSurfaceForm({ tournament, closeModal }: { tournament: RouterOutputs["tournament"]["getTournament"]; closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.tournament.updateSpecs.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournament.getTournament.queryFilter());
      closeModal();
    },
  }));

  const tournamentSurfaceForm = useForm<TournamentSurfaceSchema>({
    resolver: zodResolver(tournamentSurfaceSchema),
    defaultValues: {
      tournamentSurface: tournament.tournamentSurface || undefined,
    },
  });

  const onSubmit = tournamentSurfaceForm.handleSubmit((data) => {
    mutate({ tournamentId: tournament.id, specs: { ...data } });
  });

  return (
    <>
      <SheetForm id="TournamentSurfaceForm" onSubmit={onSubmit}>
        <Controller
          control={tournamentSurfaceForm.control}
          name="tournamentSurface"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("profile:surface")}</Label>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {playerPreferredSurface.map(surface => (
                      <SelectItem key={surface} value={surface}>{t(`profile:${surface}`)}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />
      </SheetForm>

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="TournamentSurfaceForm" loading={isPending || tournamentSurfaceForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

function TournamentHostingForm({ tournament, closeModal }: { tournament: RouterOutputs["tournament"]["getTournament"]; closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.tournament.updateSpecs.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournament.getTournament.queryFilter());
      closeModal();
    },
  }));

  const tournamentHostingForm = useForm<TournamentHostingSchema>({
    resolver: zodResolver(tournamentHostingSchema),
    defaultValues: {
      tournamentHosting: tournament.tournamentHosting || undefined,
    },
  });

  const onSubmit = tournamentHostingForm.handleSubmit((data) => {
    mutate({ tournamentId: tournament.id, specs: { ...data } });
  });

  return (
    <>
      <SheetForm id="TournamentHostingForm" onSubmit={onSubmit}>
        <Controller
          control={tournamentHostingForm.control}
          name="tournamentHosting"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("profile:hosting")}</Label>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {clubHosting.map(hosting => (
                      <SelectItem key={hosting} value={hosting}>{t(`profile:${hosting}`)}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />
      </SheetForm>

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="TournamentHostingForm" loading={isPending || tournamentHostingForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

function TournamentTravelingForm({ tournament, closeModal }: { tournament: RouterOutputs["tournament"]["getTournament"]; closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.tournament.updateSpecs.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournament.getTournament.queryFilter());
      closeModal();
    },
  }));

  const tournamentTravelingForm = useForm<TournamentTravelingSchema>({
    resolver: zodResolver(tournamentTravelingSchema),
    defaultValues: {
      tournamentTraveling: tournament.tournamentTraveling || undefined,
    },
  });

  const onSubmit = tournamentTravelingForm.handleSubmit((data) => {
    mutate({ tournamentId: tournament.id, specs: { ...data } });
  });

  return (
    <>
      <SheetForm id="TournamentTravelingForm" onSubmit={onSubmit}>
        <Controller
          control={tournamentTravelingForm.control}
          name="tournamentTraveling"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("profile:traveling")}</Label>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {clubTraveling.map(traveling => (
                      <SelectItem key={traveling} value={traveling}>{t(`profile:${traveling}`)}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />
      </SheetForm>

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="TournamentTravelingForm" loading={isPending || tournamentTravelingForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

export function TournamentSpec({ tournament }: { tournament: RouterOutputs["tournament"]["getTournament"] }) {
  const { user } = useMe();
  const [modalState, updateModalState] = useState<TournamentSpecsType | "close">("close");
  const isModalOpen = modalState !== "close";
  const { t } = useTranslation(["profile"]);

  const labels: Record<TournamentSpecsType, string> = {
    "tournament-surface": t("profile:surface"),
    "tournament-hosting": t("profile:hosting"),
    "tournament-traveling": t("profile:traveling"),
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <h3 className="p-2 font-semibold leading-[1.1] text-neutral-500">
          {t("profile:specs")}
        </h3>

        <div className="flex flex-col gap-2 rounded-[20px] bg-white p-2">
          <SpecButton
            data={tournament.tournamentSurface}
            spec="tournament-surface"
            data-color={tournament.tournamentSurface ? tournament.tournamentSurface.includes("hard") ? "blue" : "orange" : undefined}
            onClick={() => updateModalState("tournament-surface")}
          />
          <SpecButton data={tournament.tournamentHosting} spec="tournament-hosting" onClick={() => updateModalState("tournament-hosting")} />
          <SpecButton data={tournament.tournamentTraveling} spec="tournament-traveling" onClick={() => updateModalState("tournament-traveling")} />
        </div>
      </div>

      {user.role === "club" && (
        <Sheet open={isModalOpen} onOpenChange={() => updateModalState("close")}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>{isModalOpen && labels[modalState]}</SheetTitle>
            </SheetHeader>

            {modalState === "tournament-surface" && <TournamentSurfaceForm tournament={tournament} closeModal={() => updateModalState("close")} />}
            {modalState === "tournament-hosting" && <TournamentHostingForm tournament={tournament} closeModal={() => updateModalState("close")} />}
            {modalState === "tournament-traveling" && <TournamentTravelingForm tournament={tournament} closeModal={() => updateModalState("close")} />}
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}
